// eslint-disable-next-line max-classes-per-file
import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import Big from 'big.js';

import { getGlobalSpecialsSettings } from './utilities';

const BigRoundEnums = {
  ROUND_DOWN: 0,
  ROUND_HALF_UP: 1,
  ROUND_HALF_EVEN: 2,
  ROUND_UP: 3,
};

class DetailBase {
  constructor() {
    this.remainder = Big(0);
  }

  addDataValue({ type, subtype, value } = {}) {
    // this will round decimals to the nearest cent
    //
    // Might need to deal with remainders across the subtype of sales tax
    // that are vape taxes and the rest of sales taxes.
    const intValue = value.round(0, BigRoundEnums.ROUND_HALF_UP);

    this[type] = this[type] || {};
    this[type][subtype] = (this[type][subtype] || Big(0)).plus(intValue);
    const remainder = value.minus(intValue);
    this[type].remainder = (this[type].remainder || Big(0)).add(remainder);
  }

  addDataArray({ type, subtype, value, id, name, additionalAttributes } = {}) {
    // this will round decimals to the nearest cent
    const intValue = value.round(0, BigRoundEnums.ROUND_HALF_UP);

    this[type] = this[type] || {};
    this[type][subtype] = this[type][subtype] || [];
    this[type][subtype].push({ id, name, value: intValue, ...additionalAttributes });
    const remainder = value.minus(intValue);
    // We actually always want to add the remainder here, whether negative or positive.
    this[type].remainder = (this[type].remainder || Big(0)).add(remainder);
  }

  addTax({ type, value }) {
    if (!value.eq(0)) {
      this.addDataValue({ type: 'taxes', subtype: type, value });
    }
  }

  addFee({ type, value }) {
    if (!value.eq(0)) {
      this.addDataValue({ type: 'fees', subtype: type, value });
    }
  }

  addReward({ type, value, id, name }) {
    if (!value.eq(0)) {
      this.addDataArray({ type: 'rewards', subtype: type, value, id, name });
    }
  }

  addDiscount({ type, value, id, name, additionalAttributes = {} }) {
    if (!value.eq(0)) {
      this.addDataArray({ type: 'discounts', subtype: type, value, id, name, additionalAttributes });
    }
  }

  addMixAndMatch({ type, value }) {
    if (!value.eq(0)) {
      this.addDataValue({ type: 'mixAndMatch', subtype: type, value });
    }
  }
}

// Export this for unit tests.  It should only be used from Receipt otherwise.
export class ReceiptDetails extends DetailBase {
  constructor() {
    super();
    this.details = {
      order: {},
      products: [],
    };
  }

  addProduct(productID, quantity, basePrice, option) {
    // DetailBase is acting like "ProductDetail" here.
    const newProduct = new DetailBase();
    newProduct.id = productID;
    newProduct.quantity = quantity;
    newProduct.basePrice = basePrice;
    newProduct.option = option;
    newProduct.total = basePrice.times(quantity).round(0, BigRoundEnums.ROUND_HALF_UP);
    this.details.products.push(newProduct);
    return newProduct;
  }
}

/* New structure to hold all of the information about the order.  All values are in cents.
 * Ultimately all of the other values returned from this function will be deprecated and removed.
 *
 * For each of taxes, fees, rewards, and discounts there are a series of attributers that provide
 * a breakdown and a total attribute that is the sum of those.
 *
 * We include the taxConfig that this was developed under because tax configs change and we don't
 * have these versioned.
 */
export class Receipt extends ReceiptDetails {
  constructor(dispensary) {
    super();
    this.summary = {
      subtotal: 0,
      taxes: 0,
      fees: 0,
      rewards: 0,
      discounts: 0,
      mixAndMatch: 0,
      total: 0,
    };
    this.specialsSettings = getGlobalSpecialsSettings(dispensary);
    this.taxConfig = dispensary.taxConfig;
  }

  static specialKeys = ['total', 'remainder'];

  static sumSection(section) {
    return _.reduce(
      section,
      (total, value, key) => total.plus(_.includes(Receipt.specialKeys, key) ? Big(0) : value),
      Big(0)
    );
  }

  static sumValueSection(section) {
    return _.reduce(
      section,
      (total, value, key) => {
        try {
          return total.plus(
            _.includes(Receipt.specialKeys, key) ? Big(0) : _.reduce(value, (tot, val) => tot.plus(val.value), Big(0))
          );
        } catch (e) {
          console.log(e);
          throw e;
        }
      },
      Big(0)
    );
  }

  static accumulateTaxes(breakdown, section) {
    _.forEach(section, (value, key) => {
      if (!_.includes(Receipt.specialKeys, key)) {
        breakdown[key] = breakdown[key] || Big(0);
        breakdown[key] = breakdown[key].plus(value);
      }
    });
  }

  finalize() {
    let totalTaxes = Big(0);
    let totalFees = Big(0);
    let totalRewards = Big(0);
    let totalDiscounts = Big(0);
    let totalMixAndMatch = Big(0);
    const taxBreakdown = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const product of this.details.products) {
      product.taxes = product.taxes || {};
      product.taxes.total = Receipt.sumSection(product.taxes);
      totalTaxes = totalTaxes
        .plus(product.taxes.total)
        .plus(product.taxes.remainder || 0)
        .round(0, BigRoundEnums.ROUND_HALF_UP);

      Receipt.accumulateTaxes(taxBreakdown, product.taxes);

      product.rewards = product.rewards || {};
      product.rewards.total = Receipt.sumValueSection(product.rewards);
      totalRewards = totalRewards
        .plus(product.rewards.total)
        .plus(product.rewards.remainder || 0)
        .round(0, BigRoundEnums.ROUND_HALF_UP);

      product.discounts = product.discounts || {};
      product.discounts.total = Receipt.sumValueSection(product.discounts).plus(product.discounts.remainder || Big(0));
      // Before, we were rounding product.discount.total before adding it to the totalDiscounts.
      // This resulted in extra pennies on totalDiscounts.
      // Now, we will add the unrounded product.discount.total to totalDiscounts
      // and THEN round totalDiscounts after we have iterated through the products and have our raw total
      totalDiscounts = totalDiscounts.plus(product.discounts.total);
      product.discounts.total = product.discounts.total.round(0, BigRoundEnums.ROUND_HALF_UP);

      product.mixAndMatch = product.mixAndMatch || {};
      // because mix and match isn't following the same pattern
      // as the other line items (yet) we need to be a bit more
      // careful here checking for null or non Big()s
      totalMixAndMatch = totalMixAndMatch.plus(product.mixAndMatch?.total || 0).round(0, BigRoundEnums.ROUND_HALF_UP);
    }

    totalTaxes = totalTaxes.plus(Receipt.sumSection(this.taxes));
    totalFees = Receipt.sumSection(this.fees);
    totalRewards = totalRewards.plus(Receipt.sumSection(this.rewards));
    totalDiscounts = totalDiscounts.round(0, BigRoundEnums.ROUND_HALF_UP).plus(Receipt.sumValueSection(this.discounts));

    const totalProducts = _.reduce(
      _.map(this.details.products, (product) =>
        product.basePrice.times(product.quantity).plus(product.remainder).round(0, BigRoundEnums.ROUND_HALF_UP)
      ),
      (total, value) => total.plus(value),
      Big(0)
    );
    this.summary.total = totalProducts.plus(totalTaxes).plus(totalFees).minus(totalRewards).minus(totalDiscounts);

    this.summary.subtotal = totalProducts;
    this.summary.taxes = totalTaxes;
    this.summary.fees = totalFees;
    this.summary.rewards = totalRewards;
    this.summary.discounts = totalDiscounts;
    this.summary.mixAndMatch = totalMixAndMatch;

    this.details.taxBreakdown = taxBreakdown;
  }

  static toJSONMapper(value) {
    if (value instanceof Big) {
      return Number(value);
    }

    if (_.isArray(value)) {
      return _.map(value, (val) => _.mapValues(val, Receipt.toJSONMapper));
    }

    if (!_.isFunction(value) && Object(value) === value) {
      return _.mapValues(value, Receipt.toJSONMapper);
    }

    return value;
  }

  toJSON() {
    const rv = _.mapValues(
      {
        specialsSettings: this.specialsSettings,
        taxConfig: this.taxConfig,
        summary: this.summary,
        details: {
          order: this.details.order,
          products: this.details.products,
          taxBreakdown: this.details.taxBreakdown,
        },
      },
      Receipt.toJSONMapper
    );

    if (!_.isNil(this.taxes)) {
      rv.details.order.taxes = _.mapValues(this.taxes, Receipt.toJSONMapper);
    }
    if (!_.isNil(this.fees)) {
      rv.details.order.fees = _.mapValues(this.fees, Receipt.toJSONMapper);
    }
    if (!_.isNil(this.rewards)) {
      rv.details.order.rewards = _.mapValues(this.rewards, Receipt.toJSONMapper);
    }
    if (!_.isNil(this.discounts)) {
      rv.details.order.discounts = _.mapValues(this.discounts, Receipt.toJSONMapper);
    }
    if (!_.isNil(this.mixAndMatch)) {
      rv.details.order.mixAndMatch = _.mapValues(this.mixAndMatch, Receipt.toJSONMapper);
    }

    return rv;
  }
}
